/**
 * The frame around every workspace page: top bar with the workspace switcher,
 * a side rail on wide screens and a tab bar on phones.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink } from "react-router";
import markUrl from "@sable/brand/assets/mark.svg?url";
import { brand } from "@sable/brand";
import { t } from "../i18n/index.ts";
import { useMe } from "../lib/queries.ts";
import { signOut } from "../lib/session.ts";
import { useCan, useWs, wsPath } from "../lib/workspace.tsx";
import { IconBox, IconChart, IconHome, IconCheck, IconChevron, IconGear, IconPlus, IconTag } from "./icons.tsx";

/** The logo as text and the tag mark; `to: null` renders it unlinked, for pages outside the router. */
export function Wordmark({ to = "/" }: { to?: string | null }) {
  const at = brand.wordmark.lastIndexOf(brand.wordmarkAccent);
  const lead = at >= 0 ? brand.wordmark.slice(0, at) : brand.wordmark;
  const accent = at >= 0 ? brand.wordmarkAccent : "";
  // The tag mark stands in for one letter, as in the logo.
  const tagAt = lead.indexOf(brand.wordmarkTagLetter);
  const before = tagAt >= 0 ? lead.slice(0, tagAt) : "";
  const after = tagAt >= 0 ? lead.slice(tagAt + brand.wordmarkTagLetter.length) : lead;
  const text = <span aria-hidden="true">{before}{tagAt >= 0 && <img src={markUrl} alt="" />}{after}<span className="accent">{accent}</span></span>;
  if (to === null) return <span className="wordmark" role="img" aria-label={brand.displayName}>{text}</span>;
  return <Link className="wordmark" to={to} aria-label={brand.displayName}>{text}</Link>;
}

function WorkspaceSwitcher() {
  const ws = useWs();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div className="ws-switch" ref={ref}>
      <button className="ws-button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span>{ws.name}</span>
        <IconChevron size={16} />
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="label">{t("nav.workspaces")}</div>
          {me.data?.workspaces.map((w) => (
            <Link key={w.id} role="menuitem" to={`/w/${w.id}/dashboard`} onClick={() => setOpen(false)}>
              <span>{w.name}</span>
              {w.id === ws.id && <IconCheck size={16} />}
            </Link>
          ))}
          <Link role="menuitem" to="/new-workspace" onClick={() => setOpen(false)}>{t("nav.newWorkspace")}</Link>
          <hr />
          {me.data && <div className="label">{me.data.user.email}</div>}
          <button role="menuitem" onClick={() => void signOut()}>{t("nav.signOut")}</button>
        </div>
      )}
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const ws = useWs();
  const finance = useCan("finance.read");
  const sales = useCan("sales.write");
  const links = [
    { to: "dashboard", label: t("nav.dashboard"), icon: <IconHome /> },
    { to: "inventory", label: t("nav.inventory"), icon: <IconBox /> },
    ...(sales ? [{ to: "sales", label: t("nav.sales"), icon: <IconTag /> }] : []),
    ...(finance ? [{ to: "finance", label: t("nav.finance"), icon: <IconChart /> }] : []),
    { to: "settings", label: t("nav.settings"), icon: <IconGear /> },
  ];
  const tabs = [links[0]!, links[1]!, { to: "items/new", label: t("nav.add"), icon: <IconPlus />, add: true }, ...links.slice(2)];
  return (
    <div className="shell">
      <header className="topbar">
        <Wordmark to={wsPath(ws, "/dashboard")} />
        <WorkspaceSwitcher />
      </header>
      <nav className="rail" aria-label="Main">
        <NavLink className="add-link" to={wsPath(ws, "/items/new")}><IconPlus />{t("nav.add")}</NavLink>
        {links.map((l) => (
          <NavLink key={l.to} to={wsPath(ws, `/${l.to}`)}>{l.icon}{l.label}</NavLink>
        ))}
      </nav>
      <main className="main">{children}</main>
      <nav className="tabbar" aria-label="Main">
        {tabs.map((l) => (
          <NavLink key={l.to} to={wsPath(ws, `/${l.to}`)} className={"add" in l ? "add" : undefined}>
            <span className="tab-icon">{l.icon}</span>
            {l.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
